import { IconButton } from '@mui/material';
import { GridColDef, getGridStringOperators } from '@mui/x-data-grid-pro';

import { IcDotSpread } from '@/assets/images';
import { excludeValues } from '@/components/DataGrid/utils';
import { getActiveLicensePlate } from '@/layouts/TabLayout/MasterVehicles/licensePlate.utils';
import { IMasterVehicle, IOffenseOption, IOffenseOptionItem } from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import { generateUniqueId, getUpdatedLabel } from '@/utils';

import { MasterVehiclePlateCell } from './components/MasterVehiclePlateCell';

const uniqueId = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { uniqueId: uId } = params.row;
    return generateUniqueId('VEH', uId);
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
  valueGetter: (_, row) => generateUniqueId('VEH', row.uniqueId),
});

const vin = (): Partial<GridColDef> => ({
  field: 'vin',
  renderCell: (params) => {
    return params.row.vin || '/';
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
  valueGetter: (_, row) => row.vin,
});

const licensePlate = (): Partial<GridColDef> => ({
  field: 'licensePlate.number',
  renderCell: (params) => {
    return <MasterVehiclePlateCell vehicle={params.row} />;
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
  valueGetter: (_, row) => getActiveLicensePlate(row)?.number,
});

const type = (filteredOptions?: IOffenseOption[]): Partial<GridColDef> => {
  const vehicleTypes = getOffenseOptionItems(
    'VEHICLE_PROPERTY_TYPES',
    filteredOptions || [],
  ) as Map<string, IOffenseOptionItem>;
  return {
    renderCell: (params) => {
      const allVehicleTypes = vehicleTypes.values();
      return params.row.type && vehicleTypes.size
        ? [...allVehicleTypes].find((value) => value.key === params.row.type)
            ?.label
        : '/';
    },
    filterable: false,
  };
};

const lastUpdated = (): Partial<GridColDef> => ({
  renderCell: (params) => getUpdatedLabel(params.row?.updatedAt),
  sortable: false,
  filterable: false,
});

const actions = (
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    row: IMasterVehicle,
  ) => void,
): Partial<GridColDef> => ({
  width: 96,
  sortable: false,
  filterable: false,
  align: 'center',
  headerAlign: 'center',
  disableColumnMenu: true,
  renderCell: (params) => (
    <IconButton
      onClick={(event) => {
        event.stopPropagation();
        openMenu?.(event, params.row);
      }}
    >
      <IcDotSpread />
    </IconButton>
  ),
});

export const masterVehicleCols = (
  _navigate?: (path: string) => void,
  _onDelete?: (row: IMasterVehicle) => void,
  filteredOptions?: IOffenseOption[],
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    row: IMasterVehicle,
  ) => void,
) => {
  return {
    uniqueId,
    'licensePlate.number': licensePlate,
    vin,
    type: () => type(filteredOptions),
    lastUpdated,
    actions: () => actions(openMenu),
  };
};
